import React from "react";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Box from "@mui/material/Box";
import { useAuth } from "@/lib/useAuth";
import { LanguageSwitcher } from "@/components/shared/ui/LanguageSwitcher";
import { useT } from "@/i18n/useT";
import ModeSwitch from "@/components/shared/ui/ModeSwitch";
import UserProfileOrCTAButtons from "./UserProfileOrSignInButton";
import MenuIcon from "@mui/icons-material/Menu";
import Logo from "@/components/shared/shell/Logo";
import { useTheme } from "@mui/material/styles";
import MenuOpenIcon from "@mui/icons-material/MenuOpen";
import { useSchool } from "@/lib/useSchool";
import { useEffectOnce } from "@/lib/useEffectOnce";
const env = process.env.NEXT_PUBLIC_ENV;

export default function Header({
  openSidebar,
  headerHeight = 64,
  showSidebarButton = true,
  compactSidebar,
}: {
  openSidebar?: () => void;
  headerHeight?: number;
  showSidebarButton?: boolean;
  compactSidebar?: boolean;
}) {
  const theme = useTheme();
  const t = useT("ui.nav");
  const { user, loading, loginWithGoogle, logout } = useAuth();
  const { school, currentUserRole, memberId } = useSchool();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  // Check if we have a valid school session (for cross-subdomain auth)
  const isLoggedIn = !!user;
  const effectiveUser = user;

  useEffectOnce(() => {
    console.log("🔐 [Header] Auth state check:", {
      hasFirebaseUser: !!user,
      isLoggedIn,
      userEmail: user?.email,
      timestamp: new Date().toISOString(),
    });
  }, []);

  const handleProfileMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const menuId = "profile-menu";
  //====================================================================
  const dev = (
    <div className="flex flex-col">
      <div className="flex">
        {"userId: " + (user ? user?.uid.slice(0, 5) + ".." : "null")}
        {" | "}
        {"user email: " + (user ? user?.email : "null")}
        {" | "}
        {"schoolId: " + (school?.id ? school?.id.slice(0, 5) + ".." : "null")}
      </div>
      <div className="flex">
        {"role: " + (currentUserRole ? currentUserRole + " " : "null")}
        {" | "}
        {"memberId: " + (memberId ? memberId.slice(0, 5) + ".." : "null")}
      </div>
    </div>
  );
  //====================================================================

  return (
    <AppBar
      id="header"
      position="fixed"
      color="default"
      elevation={0}
      sx={{ zIndex: 1300, top: 0, left: 0, right: 0, width: "100%" }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          height: headerHeight, // Use height instead of minHeight
          minHeight: headerHeight, // Keep minHeight as backup
          ...(theme.direction === "rtl" ? { pr: 0 } : { pl: 0 }),
          "@media (min-width: 600px)": {
            height: headerHeight, // Override responsive height
            minHeight: headerHeight, // Override responsive minHeight
            ...(theme.direction === "rtl" ? { pr: 0 } : { pl: 0 }),
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center" }}>
          {showSidebarButton && (
            <Box
              component="button"
              onClick={openSidebar || (() => {})}
              aria-label="Open navigation sidebar"
              sx={{
                minWidth: headerHeight,
                height: headerHeight,
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {compactSidebar ? <MenuIcon /> : <MenuOpenIcon />}
            </Box>
          )}
          <Box sx={{ marginInlineStart: 1 }}>
            <Logo />
          </Box>
        </Box>
        {env === "development" && <>{dev}</>}

        {/* Search Bar */}
        {/* <SearchBar
          value={search}
          onChange={e => setSearch(e.target.value)}
          onSubmit={handleSearch}
          placeholder={t('searchPlaceholder', { default: 'Search courses…' })}
          isRTL={isRTL}
        /> */}

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 4,
            marginRight: 2,
            // Ensure dropdown menus appear above the header
            "& .MuiSelect-root": {
              zIndex: 1302,
            },
            "& .MuiMenu-root": {
              zIndex: 1302,
            },
            "& .MuiPopper-root": {
              zIndex: 1302,
            },
          }}
        >
          {!showSidebarButton && (
            <>
              <ModeSwitch />
              <LanguageSwitcher />
            </>
          )}
          {/* Right Side */}
          {/* <ModeSwitch /> */}
          {/* Language Switcher */}
          {/* <LanguageSwitcher /> */}
          {/* User Profile or CTA Buttons */}
          <UserProfileOrCTAButtons
            user={effectiveUser}
            loading={loading}
            anchorEl={anchorEl}
            menuId={menuId}
            handleProfileMenuOpen={handleProfileMenuOpen}
            handleMenuClose={handleMenuClose}
            t={t}
            logout={logout}
            loginWithGoogle={loginWithGoogle}
          />
        </Box>
      </Toolbar>
    </AppBar>
  );
}
